import React, { useRef, useState } from "react";
import UploadPage from "./components/UploadPage";
import AnalysisPage from "./components/AnalysisPage";
import SettingsPage from "./components/SettingsPage";
import VariantsControls from "./components/VariantsControls";
import VariantsGrid from "./components/VariantsGrid";
import AuthPage from "./components/AuthPage";
import GoogleAuthCallback from "./components/GoogleAuthCallback";
import GoogleDriveCallback from "./components/GoogleDriveCallback";
import PostPage from "./components/PostPage";
import VideoStudioSection from "./components/VideoStudioSection";
import TheWebPage from "./components/TheWebPage";
import AssignmentPage from "./components/AssignmentPage";
import NeuroPage from "./components/NeuroPage";
import TeamPermissions from "./components/TeamPermissions";
import PendingApproval from "./components/PendingApproval";
import StatusHub from "./components/StatusHub";

import {
  Layout,
  theme,
  Space,
  Button,
  Modal,
  Typography,
  ConfigProvider,
  Switch,
  Row,
  Col,
  Card,
  Tooltip,
  Segmented,
  Progress,
  Alert,
  Input,
  Form,
  message,
  Table,
  Select,
  Divider,
  Tag,
  Spin,
} from "antd";
import {
  PictureOutlined,
  FileTextOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  SafetyOutlined,
  TeamOutlined,
  BulbOutlined,
  MoonOutlined,
  MinusOutlined,
  PlusOutlined,
  ExperimentOutlined,
  VideoCameraOutlined,
  ApartmentOutlined,
  SolutionOutlined,
} from "@ant-design/icons";
import {
  getActivityPreferences,
  getSignedAssetUrl,
  setKnowledgeWorkspaceId,
} from "./services/knowledgeStore";
import { theme as antdTheme } from "antd";
import { apiFetch } from "./services/apiClient";
import {
  buildKnowledgeBundle,
  encodeKnowledgeBundleForTransport,
  normalizeKnowledgeBundle,
} from "./services/knowledgeSync";
import {
  defaultUserSettings,
  loadUserSettings,
  saveUserSettings,
} from "./services/userSettingsStore";
import {
  initPromptLibrary,
  onPromptLibraryChange,
} from "./services/promptStore";
import {
  initSectionOverrides,
  onSectionOverridesChange,
} from "./services/sectionOverridesStore";
import {
  DEFAULT_BLOG_MODEL,
  DEFAULT_IMAGE_MODEL,
  DEFAULT_POST_ANALYSIS_MODEL,
  DEFAULT_POST_COPY_MODEL,
  DEFAULT_VARIANT_MODEL,
  DEFAULT_VIDEO_MODEL,
} from "./services/modelCatalog";
import AIService from "./services/AIService";
import { initModelStats } from "./services/modelStats";
import { GENERATION_COMPLETE_EVENT } from "./services/generationNotifications";
import {
  bootstrap,
  diagnoseAndFixWorkspaces,
  getSession,
  logout,
} from "./services/auth";
import { fetchProfile, updateProfile } from "./services/profile";
import { fetchWorkspaceApiKeys } from "./services/workspaces";

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const ADMIN_ROLE_NAME = normalizeRole(
  import.meta.env.VITE_ADMIN_ROLE || "admin",
);

const hasAdminRole = (user) => {
  if (!ADMIN_ROLE_NAME) return false;
  const candidates = [
    user?.role,
    user?.app_metadata?.role,
    user?.app_metadata?.roles,
    user?.app_metadata?.claims?.role,
    user?.app_metadata?.claims?.roles,
    user?.user_metadata?.role,
    user?.user_metadata?.roles,
  ];
  return candidates.some((entry) => {
    if (!entry) return false;
    if (Array.isArray(entry)) {
      return entry.some((item) => normalizeRole(item) === ADMIN_ROLE_NAME);
    }
    return normalizeRole(entry) === ADMIN_ROLE_NAME;
  });
};

const LoadingScreen = ({
  title = "Loading",
  subtitle = "Warming up your workspace...",
}) => (
  <div className="page-loader">
    <div className="page-loader__track" aria-hidden="true">
      <div className="page-loader__bar" />
    </div>
    <div className="page-loader__text">
      <span className="page-loader__title">{title}</span>
      <span className="page-loader__subtitle">{subtitle}</span>
    </div>
  </div>
);

const AppInner = ({
  isDarkMode,
  setIsDarkMode,
  isAdmin: isAdminFromJwt,
  session,
  sessionVersion,
  userSettings,
  setUserSettings,
  syncStatus,
  setSyncStatus,
  onLogout,
  flushUserSettings,
  workspaceFlushRef,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisCollapsed, setAnalysisCollapsed] = useState(false);
  const [contextVariants, setContextVariants] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [activePage, setActivePage] = useState("web");
  const [pageTransition, setPageTransition] = useState(false);
  const [creativesMode, setCreativesMode] = useState("statics");
  const [generationStatus, setGenerationStatus] = useState({
    stage: "idle",
    total: 0,
    completed: 0,
    message: "",
  });
  const isGenerating = generationStatus?.stage !== "idle";
  const [knowledgeExtrasVersion, setKnowledgeExtrasVersion] = useState(0);
  const [workspaceIds, setWorkspaceIds] = useState([]);
  const [workspaceList, setWorkspaceList] = useState([]);
  const [workspaceRolesById, setWorkspaceRolesById] = useState({});
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  // Single-org access: who am I and what may I do. Server re-checks everything,
  // so this only drives what the UI shows.
  const [orgAccess, setOrgAccess] = useState({
    isAdmin: false,
    isApproved: false,
    status: "",
    features: {},
    tabs: {},
    loaded: false,
  });
  const [orgAccessTick, setOrgAccessTick] = useState(0);
  // profiles.is_admin wins; the JWT claim is only a bootstrap fallback for a
  // fresh instance with no profiles row yet.
  const isAdmin = orgAccess.isAdmin || isAdminFromJwt;
  const knowledgeSyncTimer = useRef(null);
  const knowledgeRetryTimer = useRef(null);
  const lastBundleHash = useRef({});
  const [knowledgeRetryTick, setKnowledgeRetryTick] = useState(0);
  const [workspaceBundleUpdatedAtById, setWorkspaceBundleUpdatedAtById] =
    useState({});
  const [loadedWorkspaceId, setLoadedWorkspaceId] = useState(null);
  const lastSessionVersionRef = useRef(0);
  const userSettingsTimer = useRef(null);
  const [analysisProgress, setAnalysisProgress] = useState({
    active: false,
    percent: 0,
    label: "",
  });
  const modelStatsInitRef = useRef(false);
  const apiKeys = userSettings?.apiKeys || defaultUserSettings.apiKeys;
  const activityPreferences = Array.isArray(userSettings?.activityPreferences)
    ? userSettings.activityPreferences
    : [];
  const [workspaceApiKeys, setWorkspaceApiKeys] = useState(null);
  const [workspaceApiKeyStatus, setWorkspaceApiKeyStatus] = useState(null);
  const [workspaceShareApiKeys, setWorkspaceShareApiKeys] = useState(false);
  const analysisModel =
    userSettings?.analysisModel || defaultUserSettings.analysisModel;
  const postAnalysisModel =
    userSettings?.postAnalysisModel || DEFAULT_POST_ANALYSIS_MODEL;
  const blogModel = userSettings?.blogModel || DEFAULT_BLOG_MODEL;
  const variantModel =
    userSettings?.variantModel || userSettings?.analysisModel || DEFAULT_VARIANT_MODEL;
  const effectiveApiKeys = apiKeys;
  const atlascloudKey = userSettings?.atlascloudKey || "";
  const handleAtlascloudKeyChange = React.useCallback(
    (key) => {
      setUserSettings((prev) => ({ ...prev, atlascloudKey: String(key || "").trim() }));
    },
    [],
  );
  const klingKey = userSettings?.apiKeys?.kling || "";
  const handleKlingKeyChange = React.useCallback(
    (key) => {
      setUserSettings((prev) => ({
        ...prev,
        apiKeys: { ...prev.apiKeys, kling: String(key || "").trim() },
      }));
    },
    [],
  );
  const [history, setHistory] = useState([]);
  const [knowledgeDb, setKnowledgeDb] = useState([]);
  const [tags, setTags] = useState([]);
  const [postTypeTags, setPostTypeTags] = useState([]);
  const [postExamples, setPostExamples] = useState([]);
  const [postCreatives, setPostCreatives] = useState([]);
  const [accountProfile, setAccountProfile] = useState({
    email: "",
    name: "",
    company: "",
    website: "",
  });
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);
  const [brandLibrary, setBrandLibrary] = useState({
    name: "",
    message: "",
    colors: [],
    fonts: [],
    assets: [],
  });
  const [aiProfile, setAiProfile] = useState({
    instructions: "",
    personality: "",
    mustDo: "",
    avoid: "",
    referenceUrls: [],
  });
  const [analysisKnowledge, setAnalysisKnowledge] = useState({
    notes: "",
    docs: [],
  });
  const [adCopyKnowledge, setAdCopyKnowledge] = useState({
    notes: "",
    avoid: "",
    docs: [],
    headlines: Array.from({ length: 5 }, () => ""),
    primaryTexts: Array.from({ length: 5 }, () => ""),
  });
  const [blogKnowledge, setBlogKnowledge] = useState({
    notes: "",
    goals: "",
    audience: "",
    mustInclude: "",
    avoid: "",
    cta: "",
    docs: [],
  });
  const [blogSeo, setBlogSeo] = useState({
    metaTitle: "",
    metaDescription: "",
    keywords: [],
    slug: "",
    canonicalUrl: "",
    schemaMarkup: "",
  });
  const [adCopyCreatives, setAdCopyCreatives] = useState([]);
  const [promptLoad, setPromptLoad] = useState({
    model: "",
    estimatedTokens: 0,
    maxTokens: 0,
    usedPercent: 0,
    overloadThresholdPercent: 80,
    thresholdFillPercent: 0,
    isNearThreshold: false,
    isOverThreshold: false,
  });
  const [qualityControlLevel, setQualityControlLevel] = useState(
    userSettings?.generationQcLevel || defaultUserSettings.generationQcLevel,
  );
  const [variantGridColumns, setVariantGridColumns] = useState(
    userSettings?.variantGridColumns || defaultUserSettings.variantGridColumns,
  );
  const notificationSettings = React.useMemo(
    () => ({
      ...defaultUserSettings.notifications,
      ...(userSettings?.notifications || {}),
    }),
    [userSettings?.notifications],
  );
  const [notificationPermission, setNotificationPermission] = useState(() => {
    if (typeof window === "undefined" || typeof Notification === "undefined") {
      return "unsupported";
    }
    return Notification.permission;
  });
  const notificationPromptOpenRef = useRef(false);
  const notificationToneQueueRef = useRef(Promise.resolve());

  const updateNotificationSettings = React.useCallback(
    (patch) => {
      setUserSettings((prev) => {
        const prevNotifications = {
          ...defaultUserSettings.notifications,
          ...(prev?.notifications || {}),
        };
        const resolvedPatch =
          typeof patch === "function" ? patch(prevNotifications) : patch;
        if (!resolvedPatch || typeof resolvedPatch !== "object") {
          return prev;
        }
        const nextNotifications = {
          ...prevNotifications,
          ...resolvedPatch,
        };
        if (
          nextNotifications.completionAudio === prevNotifications.completionAudio &&
          nextNotifications.completionPush === prevNotifications.completionPush &&
          nextNotifications.pushPermissionAsked ===
            prevNotifications.pushPermissionAsked
        ) {
          return prev;
        }
        return {
          ...prev,
          notifications: nextNotifications,
        };
      });
    },
    [setUserSettings],
  );

  const requestNotificationPermission = React.useCallback(async () => {
    if (typeof window === "undefined" || typeof Notification === "undefined") {
      setNotificationPermission("unsupported");
      return "unsupported";
    }
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission;
    } catch (error) {
      setNotificationPermission("denied");
      return "denied";
    }
  }, []);

  const enqueueNotificationTone = React.useCallback(() => {
    if (typeof window === "undefined") return;
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return;
    notificationToneQueueRef.current = notificationToneQueueRef.current
      .catch(() => {})
      .then(async () => {
        const ctx = new AudioContextCtor();
        const now = ctx.currentTime;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.11, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(880, now);
        osc.frequency.exponentialRampToValueAtTime(660, now + 0.34);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.34);
        await new Promise((resolve) => setTimeout(resolve, 380));
        await ctx.close().catch(() => {});
      });
  }, []);

  React.useEffect(() => {
    if (!analysisResult && history.length > 0) {
      setAnalysisResult(history[0]);
    }
  }, [analysisResult, history]);

  React.useEffect(() => {
    const SIGNED_URL_REFRESH_MS = 6 * 24 * 60 * 60 * 1000;
    const isSignedUrlStale = (signedAt) => {
      if (!signedAt) return true;
      const ts = Date.parse(signedAt);
      if (!Number.isFinite(ts)) return true;
      return Date.now() - ts > SIGNED_URL_REFRESH_MS;
    };
    const resolveItemUrl = (item) => item?.url || item?.mediaUrl || "";
    const needsSignedRefresh = (item) =>
      item?.path && (!resolveItemUrl(item) || isSignedUrlStale(item?.signedAt));
    const hasMissingUrl = (items) =>
      (items || []).some((item) => needsSignedRefresh(item));
    const needsRefresh =
      knowledgeDb.some((folder) => hasMissingUrl(folder.images)) ||
      postExamples.some((folder) => hasMissingUrl(folder.images)) ||
      hasMissingUrl(brandLibrary.assets) ||
      hasMissingUrl(analysisKnowledge.docs) ||
      hasMissingUrl(adCopyKnowledge.docs) ||
      hasMissingUrl(blogKnowledge.docs) ||
      hasMissingUrl(postCreatives) ||
      hasMissingUrl(activityPreferences);
    if (!needsRefresh) return;
    let cancelled = false;
    const refreshUrls = async () => {
      const updateImages = async (items) =>
        Promise.all(
          (items || []).map(async (img) => {
            if (!needsSignedRefresh(img)) return img;
            const { url, signedAt } = await getSignedAssetUrl(img.path);
            if (!url) return img;
            const hasMediaUrlField = "mediaUrl" in img;
            return {
              ...img,
              url,
              ...(hasMediaUrlField ? { mediaUrl: url } : {}),
              signedAt: signedAt || new Date().toISOString(),
            };
          }),
        );

      const nextKnowledge = await Promise.all(
        (knowledgeDb || []).map(async (folder) => ({
          ...folder,
          images: await updateImages(folder.images),
        })),
      );
      const nextPostExamples = await Promise.all(
        (postExamples || []).map(async (folder) => ({
          ...folder,
          images: await updateImages(folder.images),
        })),
      );
      const nextBrand = {
        ...brandLibrary,
        assets: await updateImages(brandLibrary.assets || []),
      };
      const nextAnalysisDocs = await Promise.all(
        (analysisKnowledge.docs || []).map(async (doc) => {
          if (!needsSignedRefresh(doc)) return doc;
          const { url, signedAt } = await getSignedAssetUrl(doc.path);
          if (!url) return doc;
          return {
            ...doc,
            url,
            signedAt: signedAt || new Date().toISOString(),
          };
        }),
      );
      const nextAdCopyDocs = await Promise.all(
        (adCopyKnowledge.docs || []).map(async (doc) => {
          if (!needsSignedRefresh(doc)) return doc;
          const { url, signedAt } = await getSignedAssetUrl(doc.path);
          if (!url) return doc;
          return {
            ...doc,
            url,
            signedAt: signedAt || new Date().toISOString(),
          };
        }),
      );
      const nextBlogDocs = await Promise.all(
        (blogKnowledge.docs || []).map(async (doc) => {
          if (!needsSignedRefresh(doc)) return doc;
          const { url, signedAt } = await getSignedAssetUrl(doc.path);
          if (!url) return doc;
          return {
            ...doc,
            url,
            signedAt: signedAt || new Date().toISOString(),
          };
        }),
      );
      const nextPostCreatives = await updateImages(postCreatives || []);
      const nextActivityPreferences = await Promise.all(
        (activityPreferences || []).map(async (item) => {
          if (!needsSignedRefresh(item)) return item;
          const { url, signedAt } = await getSignedAssetUrl(item.path);
          if (!url) return item;
          return {
            ...item,
            mediaUrl: url,
            url,
            signedAt: signedAt || new Date().toISOString(),
          };
        }),
      );
      const activityPreferencesChanged =
        nextActivityPreferences.length !== activityPreferences.length ||
        nextActivityPreferences.some(
          (item, index) => item !== activityPreferences[index],
        );

      if (!cancelled) {
        setKnowledgeDb(nextKnowledge);
        setPostExamples(nextPostExamples);
        setBrandLibrary(nextBrand);
        setAnalysisKnowledge((prev) => ({ ...prev, docs: nextAnalysisDocs }));
        setAdCopyKnowledge((prev) => ({ ...prev, docs: nextAdCopyDocs }));
        setBlogKnowledge((prev) => ({ ...prev, docs: nextBlogDocs }));
        setPostCreatives(nextPostCreatives);
        if (activityPreferencesChanged) {
          setUserSettings((prev) => ({
            ...prev,
            activityPreferences: nextActivityPreferences,
          }));
        }
      }
    };
    refreshUrls();
    return () => {
      cancelled = true;
    };
  }, [
    knowledgeDb,
    postExamples,
    postCreatives,
    brandLibrary,
    analysisKnowledge,
    adCopyKnowledge,
    blogKnowledge,
    activityPreferences,
  ]);

  React.useEffect(() => {
    if (activePage === "native") {
      setActivePage("post");
    }
  }, [activePage]);

  React.useEffect(() => {
    setPageTransition(true);
    const timer = setTimeout(() => setPageTransition(false), 450);
    return () => clearTimeout(timer);
  }, [activePage]);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof Notification === "undefined") {
      setNotificationPermission("unsupported");
      return;
    }
    const permission = Notification.permission;
    setNotificationPermission(permission);
    if (permission === "default" || notificationSettings.pushPermissionAsked) {
      return;
    }
    updateNotificationSettings({
      pushPermissionAsked: true,
      completionPush:
        permission === "granted"
          ? notificationSettings.completionPush || true
          : false,
    });
  }, [
    notificationSettings.completionPush,
    notificationSettings.pushPermissionAsked,
    updateNotificationSettings,
  ]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const handleGenerationComplete = (event) => {
      const details = event?.detail || {};
      const title = details?.title || "Generation complete";
      const body = details?.body || "Your request finished.";

      if (notificationSettings.completionAudio) {
        enqueueNotificationTone();
      }

      if (
        notificationSettings.completionPush &&
        typeof Notification !== "undefined" &&
        Notification.permission === "granted"
      ) {
        try {
          new Notification(title, {
            body,
            tag: details?.source || "generation",
          });
        } catch (error) {
          // Browser rejected notification.
        }
      }

      const shouldAskForPush =
        typeof Notification !== "undefined" &&
        Notification.permission === "default" &&
        !notificationSettings.pushPermissionAsked;
      if (!shouldAskForPush || notificationPromptOpenRef.current) {
        return;
      }

      notificationPromptOpenRef.current = true;
      Modal.confirm({
        title: "Enable desktop notifications?",
        content:
          "Get a browser alert when generation is done. You can change this any time in Settings > Notifications.",
        okText: "Enable",
        cancelText: "Not now",
        onOk: async () => {
          updateNotificationSettings({ pushPermissionAsked: true });
          const permission = await requestNotificationPermission();
          updateNotificationSettings({
            completionPush: permission === "granted",
          });
          if (permission === "granted") {
            messageApi.success("Desktop notifications enabled.");
          } else if (permission === "denied") {
            messageApi.info(
              "Desktop notifications were blocked by your browser.",
            );
          }
        },
        onCancel: () => {
          updateNotificationSettings({
            pushPermissionAsked: true,
            completionPush: false,
          });
        },
        afterClose: () => {
          notificationPromptOpenRef.current = false;
        },
      });
    };
    window.addEventListener(GENERATION_COMPLETE_EVENT, handleGenerationComplete);
    return () =>
      window.removeEventListener(
        GENERATION_COMPLETE_EVENT,
        handleGenerationComplete,
      );
  }, [
    enqueueNotificationTone,
    messageApi,
    notificationSettings.completionAudio,
    notificationSettings.completionPush,
    notificationSettings.pushPermissionAsked,
    requestNotificationPermission,
    updateNotificationSettings,
  ]);

  React.useEffect(() => {
    if (!session?.user?.id) return;
    let cancelled = false;
    const loadActivity = async () => {
      try {
        const prefs = await getActivityPreferences();
        if (cancelled) return;
        setUserSettings((prev) => ({
          ...prev,
          activityPreferences: Array.isArray(prefs) ? prefs : [],
        }));
      } catch (error) {
        // Activity preferences load failed; ignore
      }
    };
    loadActivity();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  React.useEffect(() => {
    if (modelStatsInitRef.current) return;
    modelStatsInitRef.current = true;
    initModelStats(userSettings?.modelStats, (nextStats) => {
      setUserSettings((prev) => ({ ...prev, modelStats: nextStats }));
    });
  }, [userSettings?.modelStats]);

  React.useEffect(() => {
    if (!userSettings) return;
    if (
      userSettings.generationQcLevel &&
      userSettings.generationQcLevel !== qualityControlLevel
    ) {
      setQualityControlLevel(userSettings.generationQcLevel);
    }
    if (
      Number.isFinite(userSettings.variantGridColumns) &&
      userSettings.variantGridColumns !== variantGridColumns
    ) {
      setVariantGridColumns(userSettings.variantGridColumns);
    }
  }, [userSettings]);

  React.useEffect(() => {
    if (!session?.user?.id) return;
    let cancelled = false;
    const loadWorkspaces = async () => {
      try {
        // Auto-fix any workspace issues before loading
        await diagnoseAndFixWorkspaces().catch(() => {});
        const payload = await bootstrap();
        if (cancelled) return;
        const workspaceList = payload?.workspaces || [];
        const ids = workspaceList.map((item) => item.id).filter(Boolean);
        const activeFromProfile = payload?.active_workspace_id || null;
        const activeMeta = workspaceList.find(
          (item) => item.id === activeFromProfile,
        );
        const firstTeamWorkspace = workspaceList.find(
          (item) => !item.isPersonal,
        );
        const hasManualSelection = (() => {
          try {
            const key = `workspace_manual_${session.user.id}`;
            return window.localStorage?.getItem(key) === "1";
          } catch (error) {
            return false;
          }
        })();
        const rolesById =
          payload?.roles_by_id ||
          workspaceList.reduce((acc, item) => {
            acc[item.id] = item.role || null;
            return acc;
          }, {});
        setWorkspaceRolesById(rolesById);
        setWorkspaceIds(ids);
        setWorkspaceList(workspaceList);
        let resolvedActive = null;
        if (activeFromProfile && ids.includes(activeFromProfile)) {
          resolvedActive = activeFromProfile;
          if (
            !hasManualSelection &&
            activeMeta?.isPersonal &&
            firstTeamWorkspace?.id
          ) {
            resolvedActive = firstTeamWorkspace.id;
          }
        } else {
          resolvedActive = firstTeamWorkspace?.id || ids[0] || null;
        }
        setActiveWorkspaceId(resolvedActive);
        if (resolvedActive && resolvedActive !== activeFromProfile) {
          updateProfile({ active_workspace_id: resolvedActive }).catch(() => {});
        }
      } catch (error) {
        // ignore
      }
    };
    loadWorkspaces();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  // Single-org: resolve the caller's admin flag + capability flags. This also
  // joins a brand-new user to the org (replacing the old personal-workspace
  // auto-creation).
  React.useEffect(() => {
    if (!session?.user?.id) {
      setOrgAccess({
        isAdmin: false,
        isApproved: false,
        status: "",
        features: {},
        loaded: false,
      });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // This call also redeems an invite: an invited email is approved and
        // joined to the org here, on first login.
        const me = await apiFetch("/api/org/me");
        if (cancelled) return;
        setOrgAccess({
          isAdmin: Boolean(me?.isAdmin),
          isApproved: Boolean(me?.isApproved),
          status: me?.status || "",
          features: me?.features || {},
          tabs: me?.tabs || {},
          loaded: true,
        });
      } catch (error) {
        if (!cancelled) {
          setOrgAccess({
            isAdmin: false,
            isApproved: false,
            status: "",
            features: {},
            loaded: true,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, orgAccessTick]);

  // If the admin hid the tab the user is currently on (or their default),
  // move them to their first visible tab. Order matches the nav.
  React.useEffect(() => {
    if (!orgAccess.loaded) return;
    const order = ["web", "creatives", "post", "assignment", "neuro"];
    const visible = order.filter((t) => orgAccess.tabs?.[t] !== false);
    if (!visible.length) return; // shouldn't happen; leave as-is
    if (!visible.includes(activePage)) {
      setActivePage(visible[0]);
    }
  }, [orgAccess.loaded, orgAccess.tabs, activePage]);

  React.useEffect(() => {
    if (!session?.user?.id || !activeWorkspaceId) {
      setWorkspaceShareApiKeys(false);
      setWorkspaceApiKeys(null);
      setWorkspaceApiKeyStatus(null);
      return;
    }
    let cancelled = false;
    const loadWorkspaceApiKeys = async () => {
      try {
        const payload = await fetchWorkspaceApiKeys(activeWorkspaceId);
        if (cancelled) return;
        setWorkspaceApiKeys(payload?.data || null);
        setWorkspaceApiKeyStatus(payload?.status || null);
        setWorkspaceShareApiKeys(Boolean(payload?.hasAny));
      } catch (error) {
        if (!cancelled) {
          setWorkspaceShareApiKeys(false);
          setWorkspaceApiKeys(null);
          setWorkspaceApiKeyStatus(null);
        }
      }
    };
    loadWorkspaceApiKeys();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, activeWorkspaceId]);

  React.useEffect(() => {
    if (!accountOpen || !session?.user?.id) return;
    let cancelled = false;
    const loadAccountProfile = async () => {
      setAccountLoading(true);
      try {
        let payload = await fetchProfile();
        let profile = payload?.profile || null;
        if (!profile) {
          const created = await updateProfile({
            name: "",
            company: "",
            website: "",
          });
          profile = created?.profile || null;
        }
        if (cancelled) return;
        setAccountProfile({
          email: profile?.email || session.user.email || "",
          name: profile?.name || "",
          company: profile?.company || "",
          website: profile?.website || "",
        });
      } catch (error) {
        if (!cancelled) {
          messageApi.error(error?.message || "Failed to load account profile");
        }
      } finally {
        if (!cancelled) setAccountLoading(false);
      }
    };
    loadAccountProfile();
    return () => {
      cancelled = true;
    };
  }, [accountOpen, session?.user?.id, messageApi]);

  const handleSaveAccountProfile = async () => {
    if (!session?.user?.id) return;
    setAccountSaving(true);
    try {
      const payload = {
        name: accountProfile.name.trim(),
        company: accountProfile.company.trim(),
        website: accountProfile.website.trim(),
      };
      await updateProfile(payload);
      setAccountProfile((prev) => ({
        ...prev,
        name: payload.name,
        company: payload.company,
        website: payload.website,
      }));
      messageApi.success("Account profile saved");
    } catch (error) {
      messageApi.error(error?.message || "Failed to save account profile");
    } finally {
      setAccountSaving(false);
    }
  };

  const handleWorkspaceChange = async (workspaceId) => {
    if (!session?.user?.id || !workspaceId) return;
    try {
      const storageKey = `workspace_manual_${session.user.id}`;
      window.localStorage?.setItem(storageKey, "1");
    } catch (error) {
      // ignore storage failures
    }
    setActiveWorkspaceId(workspaceId);
    try {
      await updateProfile({ active_workspace_id: workspaceId });
    } catch (error) {
      // Silently fail - workspace is still switched locally
    }
  };

  React.useEffect(() => {
    setKnowledgeWorkspaceId(activeWorkspaceId);
    setLoadedWorkspaceId(null);
  }, [activeWorkspaceId]);

  const getKnowledgeBackupKey = React.useCallback(
    (workspaceId) => {
      if (!session?.user?.id || !workspaceId) return "";
      return `knowledge_backup_v1:${session.user.id}:${workspaceId}`;
    },
    [session?.user?.id],
  );

  const saveKnowledgeBackup = React.useCallback(
    (workspaceId, bundle) => {
      if (typeof window === "undefined") return;
      const key = getKnowledgeBackupKey(workspaceId);
      if (!key || !bundle || typeof bundle !== "object") return;
      try {
        const payload = JSON.stringify({
          savedAt: new Date().toISOString(),
          bundle,
        });
        window.sessionStorage?.setItem(key, payload);
        window.localStorage?.setItem(key, payload);
      } catch (error) {
        // ignore backup write failures (quota, privacy mode, etc.)
      }
    },
    [getKnowledgeBackupKey],
  );

  const loadKnowledgeBackup = React.useCallback(
    (workspaceId) => {
      if (typeof window === "undefined") return null;
      const key = getKnowledgeBackupKey(workspaceId);
      if (!key) return null;
      const parse = (raw) => {
        if (!raw) return null;
        try {
          const parsed = JSON.parse(raw);
          const bundle = parsed?.bundle || parsed;
          return bundle && typeof bundle === "object" ? bundle : null;
        } catch (error) {
          return null;
        }
      };
      const sessionBundle = parse(window.sessionStorage?.getItem(key));
      if (sessionBundle) return sessionBundle;
      return parse(window.localStorage?.getItem(key));
    },
    [getKnowledgeBackupKey],
  );

  const refreshWorkspaceBundle = React.useCallback(
    async ({ force = false } = {}) => {
      if (!session?.user?.id) return;
      const ids = workspaceIds || [];
      const targetWorkspace = activeWorkspaceId || ids[0];
      if (!targetWorkspace) return;
      const backupBundle = loadKnowledgeBackup(targetWorkspace);
      const backupTs = new Date(backupBundle?.updatedAt || 0).getTime();
      try {
        const payload = await apiFetch(
          `/api/knowledge/bundle?workspace_id=${encodeURIComponent(
            targetWorkspace,
          )}`,
        );
        const data = payload?.bundle || null;
        const bundlePayload = data?.data;
        const forceReload =
          sessionVersion &&
          sessionVersion !== lastSessionVersionRef.current;
        const isDifferentWorkspace = loadedWorkspaceId !== targetWorkspace;
        if (!bundlePayload || typeof bundlePayload !== "object") {
          if (backupBundle && !knowledgeSyncTimer.current) {
            if (loadedWorkspaceId !== targetWorkspace || force || forceReload) {
              applyKnowledgeBundle(backupBundle, targetWorkspace, {
                markSaved: false,
              });
            }
            if (forceReload) {
              lastSessionVersionRef.current = sessionVersion;
            }
            return;
          }
          if (force || forceReload || isDifferentWorkspace) {
            applyKnowledgeBundle({ data: {} }, targetWorkspace);
            if (forceReload) {
              lastSessionVersionRef.current = sessionVersion;
            }
          } else {
            setLoadedWorkspaceId(targetWorkspace);
            setWorkspaceBundleUpdatedAtById((prev) => ({
              ...prev,
              [targetWorkspace]: data?.updated_at || null,
            }));
          }
          return;
        }
        const localUpdatedAt =
          workspaceBundleUpdatedAtById?.[targetWorkspace] || null;
        const serverTs = new Date(data.updated_at || 0).getTime();
        const localTs = new Date(localUpdatedAt || 0).getTime();
        if (backupBundle && backupTs > serverTs && !knowledgeSyncTimer.current) {
          if (loadedWorkspaceId === targetWorkspace && !force && !forceReload) {
            // Current state is already loaded and may contain unsaved edits;
            // don't overwrite it with a potentially stale backup.
            // Just update the server timestamp so the next refresh is clean.
            setWorkspaceBundleUpdatedAtById((prev) => ({
              ...prev,
              [targetWorkspace]: data?.updated_at || null,
            }));
          } else {
            applyKnowledgeBundle(backupBundle, targetWorkspace, {
              markSaved: false,
            });
          }
          if (forceReload) {
            lastSessionVersionRef.current = sessionVersion;
          }
          return;
        }
        if (
          force ||
          forceReload ||
          isDifferentWorkspace ||
          !localUpdatedAt ||
          serverTs > localTs
        ) {
          applyKnowledgeBundle(
            {
              ...bundlePayload,
              updatedAt: data.updated_at || bundlePayload.updatedAt,
            },
            targetWorkspace,
          );
          saveKnowledgeBackup(targetWorkspace, {
            ...bundlePayload,
            updatedAt: data.updated_at || bundlePayload.updatedAt,
          });
          if (forceReload) {
            lastSessionVersionRef.current = sessionVersion;
          }
        }
      } catch (error) {
        if (backupBundle) {
          applyKnowledgeBundle(backupBundle, targetWorkspace, {
            markSaved: false,
          });
          setSyncStatus?.((prev) => ({
            ...prev,
            lastError:
              "Loaded workspace knowledge from backup because server sync failed.",
          }));
        }
      }
    },
    [
      session?.user?.id,
      workspaceIds,
      activeWorkspaceId,
      loadedWorkspaceId,
      workspaceBundleUpdatedAtById,
      sessionVersion,
      loadKnowledgeBackup,
      saveKnowledgeBackup,
    ],
  );

  React.useEffect(() => {
    refreshWorkspaceBundle();
  }, [refreshWorkspaceBundle]);

  // Refresh the workspace knowledge bundle when the tab regains focus rather
  // than polling every 30s — a 200KB bundle polled continuously would exhaust
  // the Supabase free-tier egress budget within days. Focus-based refresh is
  // plenty fresh for a small internal team.
  React.useEffect(() => {
    if (!session?.user?.id) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (settingsOpen) return;
      refreshWorkspaceBundle();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [session?.user?.id, settingsOpen, refreshWorkspaceBundle]);

  const flushWorkspaceData = React.useCallback(async () => {
    if (!session?.user?.id || !knowledgeSyncTimer.current) return;
    const ids = workspaceIds || [];
    const targetWorkspace = activeWorkspaceId || ids[0];
    if (!targetWorkspace) return;
    clearTimeout(knowledgeSyncTimer.current);
    knowledgeSyncTimer.current = null;
    try {
      const contentHash = JSON.stringify({
        knowledgeDb,
        tags,
        postTypeTags,
        postExamples,
        postCreatives,
        brandLibrary,
        aiProfile,
        analysisKnowledge,
        adCopyKnowledge,
        blogKnowledge,
        blogSeo,
        history,
        adCopyCreatives,
        activityPreferences: userSettings?.activityPreferences || [],
      });
      const lastHash = lastBundleHash.current?.[targetWorkspace] || "";
      if (contentHash === lastHash) return;
      const bundle = buildKnowledgeBundle({
        knowledgeDb,
        tags,
        postTypeTags,
        postExamples,
        postCreatives,
        brandLibrary,
        aiProfile,
        analysisKnowledge,
        adCopyKnowledge,
        blogKnowledge,
        blogSeo,
        history,
        adCopyCreatives,
        activityPreferences: userSettings?.activityPreferences || [],
      });
      saveKnowledgeBackup(targetWorkspace, bundle);
      const { data: transportData, dataEncoding } =
        await encodeKnowledgeBundleForTransport(bundle);
      const payload = {
        workspace_id: targetWorkspace,
        data: transportData,
        updated_at: bundle.updatedAt,
      };
      if (dataEncoding) {
        payload.data_encoding = dataEncoding;
      }
      setSyncStatus?.((prev) => ({
        ...prev,
        workspaceSaving: true,
        lastError: "",
      }));
      await apiFetch(
        `/api/knowledge/bundle?workspace_id=${encodeURIComponent(
          targetWorkspace,
        )}`,
        {
          method: "PUT",
          body: payload,
        },
      );
      lastBundleHash.current = {
        ...lastBundleHash.current,
        [targetWorkspace]: contentHash,
      };
      if (knowledgeRetryTimer.current) {
        clearTimeout(knowledgeRetryTimer.current);
        knowledgeRetryTimer.current = null;
      }
      setWorkspaceBundleUpdatedAtById((prev) => ({
        ...prev,
        [targetWorkspace]: bundle.updatedAt,
      }));
      setSyncStatus?.((prev) => ({
        ...prev,
        workspaceSaving: false,
        lastWorkspaceSyncAt: new Date().toISOString(),
      }));
    } catch (error) {
      if (typeof window !== "undefined" && !knowledgeRetryTimer.current) {
        knowledgeRetryTimer.current = window.setTimeout(() => {
          knowledgeRetryTimer.current = null;
          setKnowledgeRetryTick((prev) => prev + 1);
        }, 3000);
      }
      setSyncStatus?.((prev) => ({
        ...prev,
        workspaceSaving: false,
        lastError: error?.message || "Failed to save workspace data",
      }));
    }
  }, [
    knowledgeDb,
    tags,
    postTypeTags,
    postExamples,
    postCreatives,
    brandLibrary,
    aiProfile,
    analysisKnowledge,
    adCopyKnowledge,
    blogKnowledge,
    blogSeo,
    history,
    adCopyCreatives,
    session?.user?.id,
    workspaceIds,
    activeWorkspaceId,
    userSettings?.activityPreferences,
    saveKnowledgeBackup,
    knowledgeRetryTick,
  ]);

  // Expose flushWorkspaceData to outer App via ref
  React.useEffect(() => {
    if (workspaceFlushRef) {
      workspaceFlushRef.current = flushWorkspaceData;
    }
  }, [flushWorkspaceData, workspaceFlushRef]);

  React.useEffect(() => {
    if (!session?.user?.id) return;
    const ids = workspaceIds || [];
    const targetWorkspace = activeWorkspaceId || ids[0];
    if (!targetWorkspace) return;
    if (loadedWorkspaceId !== targetWorkspace) return;
    if (knowledgeSyncTimer.current) {
      clearTimeout(knowledgeSyncTimer.current);
    }
    knowledgeSyncTimer.current = setTimeout(async () => {
      try {
        const contentHash = JSON.stringify({
          knowledgeDb,
          tags,
          postTypeTags,
          postExamples,
          postCreatives,
          brandLibrary,
          aiProfile,
          analysisKnowledge,
          adCopyKnowledge,
          blogKnowledge,
          blogSeo,
          history,
          adCopyCreatives,
          activityPreferences: userSettings?.activityPreferences || [],
        });
        const lastHash = lastBundleHash.current?.[targetWorkspace] || "";
        if (contentHash === lastHash) return;
        const bundle = buildKnowledgeBundle({
          knowledgeDb,
          tags,
          postTypeTags,
          postExamples,
          postCreatives,
          brandLibrary,
          aiProfile,
          analysisKnowledge,
          adCopyKnowledge,
          blogKnowledge,
          blogSeo,
          history,
          adCopyCreatives,
          activityPreferences: userSettings?.activityPreferences || [],
        });
        saveKnowledgeBackup(targetWorkspace, bundle);
        const { data: transportData, dataEncoding } =
          await encodeKnowledgeBundleForTransport(bundle);
        const payload = {
          workspace_id: targetWorkspace,
          data: transportData,
          updated_at: bundle.updatedAt,
        };
        if (dataEncoding) {
          payload.data_encoding = dataEncoding;
        }
        setSyncStatus?.((prev) => ({
          ...prev,
          workspaceSaving: true,
          lastError: "",
        }));
        await apiFetch(
          `/api/knowledge/bundle?workspace_id=${encodeURIComponent(
            targetWorkspace,
          )}`,
          {
            method: "PUT",
            body: payload,
          },
        );
        lastBundleHash.current = {
          ...lastBundleHash.current,
          [targetWorkspace]: contentHash,
        };
        if (knowledgeRetryTimer.current) {
          clearTimeout(knowledgeRetryTimer.current);
          knowledgeRetryTimer.current = null;
        }
        setWorkspaceBundleUpdatedAtById((prev) => ({
          ...prev,
          [targetWorkspace]: bundle.updatedAt,
        }));
        setSyncStatus?.((prev) => ({
          ...prev,
          workspaceSaving: false,
          lastWorkspaceSyncAt: new Date().toISOString(),
        }));
      } catch (error) {
        if (typeof window !== "undefined" && !knowledgeRetryTimer.current) {
          knowledgeRetryTimer.current = window.setTimeout(() => {
            knowledgeRetryTimer.current = null;
            setKnowledgeRetryTick((prev) => prev + 1);
          }, 3000);
        }
        setSyncStatus?.((prev) => ({
          ...prev,
          workspaceSaving: false,
          lastError: error?.message || "Failed to save workspace data",
        }));
      }
    }, 600);
    return () => {
      if (knowledgeSyncTimer.current) {
        clearTimeout(knowledgeSyncTimer.current);
      }
    };
  }, [
    knowledgeDb,
    tags,
    postTypeTags,
    postExamples,
    postCreatives,
    brandLibrary,
    aiProfile,
    analysisKnowledge,
    adCopyKnowledge,
    blogKnowledge,
    blogSeo,
    history,
    adCopyCreatives,
    session?.user?.id,
    workspaceIds,
    knowledgeExtrasVersion,
    activeWorkspaceId,
    loadedWorkspaceId,
    userSettings?.activityPreferences,
    saveKnowledgeBackup,
    knowledgeRetryTick,
  ]);

  React.useEffect(() => {
    return () => {
      if (knowledgeRetryTimer.current) {
        clearTimeout(knowledgeRetryTimer.current);
        knowledgeRetryTimer.current = null;
      }
    };
  }, []);

  // Page Visibility API to flush saves when page becomes hidden
  React.useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Page is hidden, flush any pending saves
        await Promise.allSettled([flushUserSettings(), flushWorkspaceData()]);
      }
    };

    const handleBeforeUnload = async () => {
      // Flush saves before page unload
      await Promise.allSettled([flushUserSettings(), flushWorkspaceData()]);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [flushUserSettings, flushWorkspaceData]);

  React.useEffect(() => {
    setUserSettings((prev) => ({ ...prev, uiDarkMode: isDarkMode }));
  }, [isDarkMode]);

  React.useEffect(() => {
    setUserSettings((prev) => ({
      ...prev,
      generationQcLevel: qualityControlLevel,
    }));
  }, [qualityControlLevel]);

  React.useEffect(() => {
    setUserSettings((prev) => ({
      ...prev,
      variantGridColumns,
    }));
  }, [variantGridColumns]);

  // Workspace data is persisted via Supabase (knowledge_bundles)

  const applyKnowledgeBundle = (bundle, workspaceId, options = {}) => {
    const { markSaved = true } = options;
    const normalized = normalizeKnowledgeBundle(bundle);
    const resolvedWorkspaceId = workspaceId || activeWorkspaceId || null;
    if (resolvedWorkspaceId && markSaved) {
      lastBundleHash.current = {
        ...lastBundleHash.current,
        [resolvedWorkspaceId]: JSON.stringify({
          knowledgeDb: normalized.knowledgeDb,
          tags: normalized.tags,
          postTypeTags: normalized.postTypeTags,
          postExamples: normalized.postExamples,
          postCreatives: normalized.postCreatives,
          brandLibrary: normalized.brandLibrary,
          aiProfile: normalized.aiProfile,
          analysisKnowledge: normalized.analysisKnowledge,
          adCopyKnowledge: normalized.adCopyKnowledge,
          blogKnowledge: normalized.blogKnowledge,
          blogSeo: normalized.blogSeo,
          history: normalized.history || [],
          adCopyCreatives: normalized.adCopyCreatives || [],
          activityPreferences: normalized.activityPreferences || [],
        }),
      };
    }
    setKnowledgeDb(normalized.knowledgeDb);
    setTags(normalized.tags);
    setPostTypeTags(normalized.postTypeTags);
    setPostExamples(normalized.postExamples);
    setPostCreatives(normalized.postCreatives);
    setBrandLibrary(normalized.brandLibrary);
    setAiProfile(normalized.aiProfile);
    setAnalysisKnowledge(normalized.analysisKnowledge);
    setAdCopyKnowledge(normalized.adCopyKnowledge);
    setBlogKnowledge(normalized.blogKnowledge);
    setBlogSeo(normalized.blogSeo);
    setHistory(normalized.history || []);
    setAdCopyCreatives(normalized.adCopyCreatives || []);
    setUserSettings((prev) => ({
      ...prev,
      activityPreferences: normalized.activityPreferences || [],
    }));
    if (resolvedWorkspaceId) {
      setWorkspaceBundleUpdatedAtById((prev) => ({
        ...prev,
        [resolvedWorkspaceId]: bundle?.updatedAt || null,
      }));
      saveKnowledgeBackup(resolvedWorkspaceId, {
        ...normalized,
        updatedAt: bundle?.updatedAt || new Date().toISOString(),
      });
    }
    setLoadedWorkspaceId(resolvedWorkspaceId);
    setKnowledgeExtrasVersion((prev) => prev + 1);
  };

  // Assets are stored in Supabase storage; URLs are stored in workspace state.

  const saveToHistory = (newAnalysis) => {
    setHistory((prev) => {
      const updatedHistory = [newAnalysis, ...prev];
      return updatedHistory;
    });
  };

  const updateAnalysisEntry = React.useCallback((analysisId, updater) => {
    if (!analysisId || typeof updater !== "function") return;
    setHistory((prev) =>
      prev.map((item) => (item.id === analysisId ? updater(item) : item)),
    );
    setAnalysisResult((prev) =>
      prev?.id === analysisId ? updater(prev) : prev,
    );
  }, []);

  const {
    token: {
      colorBgContainer,
      borderRadiusLG,
      colorBgLayout,
      colorBgElevated,
      colorTextSecondary,
      colorSplit,
    },
  } = theme.useToken();

  React.useEffect(() => {
    document.body.style.backgroundColor = colorBgLayout;
    document.body.dataset.theme = isDarkMode ? "dark" : "light";
  }, [colorBgLayout, isDarkMode]);

  const creativesContent = (
    <>
      <Segmented
        value={creativesMode}
        onChange={setCreativesMode}
        style={{ marginBottom: 16 }}
        options={[
          {
            label: (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <PictureOutlined />
                Statics
              </span>
            ),
            value: "statics",
          },
          {
            label: (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <VideoCameraOutlined />
                Video Studio
              </span>
            ),
            value: "videos",
          },
        ]}
      />

      {creativesMode === "videos" ? null : (
      <>
      <Row gutter={[16, 16]} align="stretch" className="top-row">
        <Col xs={24} lg={8} style={{ display: "flex" }}>
          <Card
            size="small"
            title="Upload"
            style={{ flex: 1 }}
            className="panel-card"
          >
            <UploadPage
              showCard={false}
              apiKeys={{
                ...effectiveApiKeys,
                aiProfile,
                analysisKnowledge,
                adCopyKnowledge,
                tagProfile: tags,
                generationAspectRatio: userSettings?.generationAspectRatio,
              }}
              workspaceId={activeWorkspaceId}
              analysisModel={analysisModel}
              onAnalysisModelChange={(value) =>
                setUserSettings((prev) => ({
                  ...prev,
                  analysisModel: value,
                  variantModel: value,
                }))
              }
              onAnalysisComplete={(data) => {
                const entry = {
                  ...data,
                  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                  createdAt: new Date().toISOString(),
                };
                saveToHistory(entry);
                setAnalysisResult(entry);
                setContextVariants([]);
                setGenerationStatus({
                  stage: "idle",
                  total: 0,
                  completed: 0,
                  message: "",
                });
              }}
              onAnalysisProgress={setAnalysisProgress}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8} style={{ display: "flex" }}>
          <Card
            size="small"
            title="Generate"
            extra={
              <Tooltip
                title={`Prompt load: ${Math.round(promptLoad.usedPercent)}% of context (${promptLoad.estimatedTokens.toLocaleString()} / ${(promptLoad.maxTokens || 0).toLocaleString()} est. tokens). Threshold: ${Math.round(promptLoad.overloadThresholdPercent || 80)}%`}
              >
                <span className="context-window-indicator-wrap">
                  <span
                    className={`context-window-indicator${
                      promptLoad.isOverThreshold
                        ? " is-danger"
                        : promptLoad.isNearThreshold
                          ? " is-warn"
                          : ""
                    }`}
                    style={{
                      "--fill-percent": `${Math.max(
                        0,
                        Math.min(100, promptLoad.thresholdFillPercent),
                      )}%`,
                    }}
                    aria-label={`Prompt load ${Math.round(promptLoad.usedPercent)}% used`}
                  />
                </span>
              </Tooltip>
            }
            style={{ flex: 1 }}
            className="panel-card panel-card--focus"
          >
            <VariantsControls
              showCard={false}
              analysis={analysisResult}
              history={history}
              onSelectAnalysis={(item) => {
                setAnalysisResult(item);
                setContextVariants([]);
                setGenerationStatus({
                  stage: "idle",
                  total: 0,
                  completed: 0,
                  message: "",
                });
              }}
              onVariantsChange={setContextVariants}
              knowledgeDb={knowledgeDb}
              tags={tags}
              brandLibrary={brandLibrary}
              aiProfile={aiProfile}
              analysisKnowledge={analysisKnowledge}
              adCopyKnowledge={adCopyKnowledge}
              apiKeys={effectiveApiKeys}
              analysisModel={analysisModel}
              variantModel={variantModel}
              generationAspectRatio={userSettings?.generationAspectRatio}
              generationTagIds={userSettings?.generationTagIds}
              knowledgeActiveIds={userSettings?.knowledgeActiveIds}
              generationSourceMode={userSettings?.generationSourceMode}
              generationSceneMode={userSettings?.generationSceneMode}
              specialInstructions={userSettings?.specialInstructions}
              specialInstructionMode={userSettings?.specialInstructionMode}
              specialInstructionNote={userSettings?.specialInstructionNote}
              onPreferencesChange={(patch) =>
                setUserSettings((prev) => ({ ...prev, ...patch }))
              }
              qualityControlLevel={qualityControlLevel}
              onGenerationStatusChange={setGenerationStatus}
              onPromptLoadChange={setPromptLoad}
              disabled={isGenerating}
              workspaceId={activeWorkspaceId}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8} style={{ display: "flex" }}>
          <Card
            size="small"
            title="Analysis"
            extra={
              <Button
                type="text"
                size="small"
                icon={analysisCollapsed ? <PlusOutlined /> : <MinusOutlined />}
                onClick={() => setAnalysisCollapsed((prev) => !prev)}
                aria-label={analysisCollapsed ? "Expand analysis" : "Collapse analysis"}
              />
            }
            style={{ flex: 1 }}
            className="panel-card"
            bodyStyle={analysisCollapsed ? { display: "none" } : undefined}
          >
            <AnalysisPage
              showCard={false}
              analysis={analysisResult}
              history={history}
              tags={tags}
              onTagsChange={setTags}
              onAnalysisUpdate={updateAnalysisEntry}
              onAdCopyKnowledgeChange={setAdCopyKnowledge}
              apiKeys={effectiveApiKeys}
              aiProfile={aiProfile}
              analysisKnowledge={analysisKnowledge}
              adCopyKnowledge={adCopyKnowledge}
              analysisModel={analysisModel}
              generationAspectRatio={userSettings?.generationAspectRatio}
              workspaceId={activeWorkspaceId}
              onSelectAnalysis={(item) => {
                setAnalysisResult(item);
                setContextVariants([]);
                setGenerationStatus({
                  stage: "idle",
                  total: 0,
                  completed: 0,
                  message: "",
                });
              }}
            />
          </Card>
        </Col>
      </Row>
      <div style={{ marginTop: 16 }}>
        <VariantsGrid
          variants={contextVariants}
          generationStatus={generationStatus}
          columns={variantGridColumns}
          onColumnsChange={setVariantGridColumns}
          qualityControlLevel={qualityControlLevel}
          onQualityControlLevelChange={setQualityControlLevel}
          adCopyCreatives={adCopyCreatives}
          onAdCopyCreativesChange={setAdCopyCreatives}
          postCreatives={postCreatives}
          onPostCreativesChange={setPostCreatives}
          activityPreferences={userSettings?.activityPreferences}
          onActivityPreferencesChange={(next) =>
            setUserSettings((prev) => ({
              ...prev,
              activityPreferences: Array.isArray(next) ? next : [],
            }))
          }
          onAnalyzeVariant={async (variant) => {
            const primaryVideoUrl =
              variant.stitchedVideoUrl || variant.videoUrl || "";
            const mediaUrl = primaryVideoUrl || variant.imageUrl || "";
            if (!mediaUrl) return;
            try {
              const aiService = new AIService({
                aiProfile,
                analysisKnowledge,
                adCopyKnowledge,
                tags,
                workspaceId: activeWorkspaceId,
              });
              const resolvedMedia =
                variant?.blob instanceof Blob
                  ? {
                      blob: variant.blob,
                      mimeType:
                        variant.mimeType ||
                        variant.blob.type ||
                        (primaryVideoUrl ? "video/mp4" : "image/png"),
                    }
                  : await aiService.fetchMediaBlob(mediaUrl);
              const blob = resolvedMedia.blob;
              const isVideo =
                Boolean(primaryVideoUrl) ||
                String(variant?.mimeType || blob.type || "").startsWith("video") ||
                variant?.mediaType === "video";
              const file = new File(
                [blob],
                isVideo ? "variant.mp4" : "variant.png",
                {
                  type:
                    resolvedMedia.mimeType ||
                    blob.type ||
                    (isVideo ? "video/mp4" : "image/png"),
                },
              );
              const analysis = await aiService.analyzeCreative(
                file,
                isVideo,
                null,
                undefined,
                { model: analysisModel },
              );
              const masterContext = isVideo
                ? analysis?.segments
                  ? analysis
                  : analysis?.combined_context || analysis
                : analysis;
              const entry = {
                type: isVideo ? "video" : "image",
                previewUrl: mediaUrl,
                fileName: "Generated Variant",
                analysis,
                masterContext,
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                createdAt: new Date().toISOString(),
              };
              saveToHistory(entry);
              setAnalysisResult(entry);
            } catch (error) {
              console.error("Variant analysis failed", error);
              messageApi.error(
                error?.message
                  ? `Variant analysis failed: ${error.message}`
                  : "Variant analysis failed. Please try again.",
              );
            }
          }}
        />
      </div>
      </>
      )}

      {creativesMode === "videos" && (
        <VideoStudioSection
          atlascloudKey={atlascloudKey}
          onAtlascloudKeyChange={handleAtlascloudKeyChange}
          klingKey={klingKey}
          onKlingKeyChange={handleKlingKeyChange}
          knowledgeDb={knowledgeDb}
          workspaceId={activeWorkspaceId}
        />
      )}
    </>
  );

  // ACCESS GATE — checked BEFORE anything renders. While the approval answer
  // is in flight the user sees a loader, never a flash of the app; an
  // unapproved account then sees only the pending screen. This is still a
  // courtesy — every API route independently 403s an unapproved user, so
  // there is nothing to gain by bypassing it.
  if (!orgAccess.loaded) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "var(--app-bg)",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }
  if (!orgAccess.isApproved) {
    return (
      <PendingApproval
        status={orgAccess.status}
        email={session?.user?.email || ""}
        onRetry={() => setOrgAccessTick((n) => n + 1)}
        onLogout={onLogout}
      />
    );
  }

  return (
    <Layout
      className="app-shell"
      style={{ minHeight: "100vh", background: colorBgLayout }}
    >
      {contextHolder}
      <Header
        className="app-header"
        style={{
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Space size="middle" align="center" className="app-header__left">
          {workspaceList.length > 0 && (
            workspaceList.length === 1 ? (
              <Space size="small" align="center">
                <TeamOutlined style={{ color: colorTextSecondary }} />
                <Text strong>{workspaceList[0]?.name || "Workspace"}</Text>
              </Space>
            ) : (
              <Select
                value={activeWorkspaceId}
                onChange={handleWorkspaceChange}
                style={{ minWidth: 200 }}
                options={workspaceList.map((ws) => ({
                  label: (
                    <Space size="small">
                      <TeamOutlined />
                      {ws.name}
                      {ws.isPersonal && (
                        <Tag color="blue" style={{ marginLeft: 4 }}>
                          Personal
                        </Tag>
                      )}
                    </Space>
                  ),
                  value: ws.id,
                }))}
              />
            )
          )}
          <Segmented
            value={activePage}
            onChange={setActivePage}
            options={[
              {
                label: (
                  <span style={{ display: "inline-flex", gap: 6 }}>
                    <ApartmentOutlined />
                    The Web
                  </span>
                ),
                value: "web",
              },
              {
                label: (
                  <span style={{ display: "inline-flex", gap: 6 }}>
                    <PictureOutlined />
                    Creatives
                  </span>
                ),
                value: "creatives",
              },
              {
                label: (
                  <span style={{ display: "inline-flex", gap: 6 }}>
                    <FileTextOutlined />
                    Copy
                  </span>
                ),
                value: "post",
              },
              {
                label: (
                  <span style={{ display: "inline-flex", gap: 6 }}>
                    <SolutionOutlined />
                    Assignment
                  </span>
                ),
                value: "assignment",
              },
              {
                label: (
                  <span style={{ display: "inline-flex", gap: 6 }}>
                    <ExperimentOutlined />
                    Neuro
                  </span>
                ),
                value: "neuro",
              },
              // A tab shows unless the admin explicitly hid it for this user.
            ].filter((opt) => orgAccess.tabs?.[opt.value] !== false)}
          />
        </Space>
        <Space size="middle" className="app-header__right">
          <Space size={6} align="center">
            <Switch
              checked={isDarkMode}
              onChange={setIsDarkMode}
              checkedChildren={<MoonOutlined />}
              unCheckedChildren={<BulbOutlined />}
              size="small"
            />
          </Space>
          {orgAccess.isAdmin || orgAccess.features?.manage_users ? (
            <Tooltip title="Team & permissions">
              <Button
                size="small"
                icon={<TeamOutlined />}
                onClick={() => setTeamOpen(true)}
                style={{ borderRadius: 8 }}
              />
            </Tooltip>
          ) : null}
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => setSettingsOpen(true)}
            style={{ borderRadius: 8 }}
          />
          <Button
            size="small"
            icon={<UserOutlined />}
            onClick={() => setAccountOpen(true)}
            style={{ borderRadius: 8 }}
          />
          <Button
            size="small"
            icon={<LogoutOutlined />}
            onClick={onLogout}
            style={{ borderRadius: 8 }}
          />
        </Space>
      </Header>
      {(pageTransition || isGenerating || analysisProgress.active) && (
        <div className="app-top-loader" aria-hidden="true">
          <div className="app-top-loader__bar" />
        </div>
      )}
      {analysisProgress.active ? (
        <div
          style={{
            background: colorBgElevated,
            padding: "6px 24px 8px",
            borderBottom: `1px solid ${colorSplit || colorBgContainer}`,
          }}
        >
          <Space direction="vertical" size={2} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12, color: colorTextSecondary }}>
              {analysisProgress.label || "Analyzing..."}
            </Text>
            <Progress
              percent={analysisProgress.percent || 0}
              showInfo={false}
              size="small"
              status="active"
            />
          </Space>
        </div>
      ) : null}
      <Content style={{ margin: "16px" }} className="app-content">
        <div
          className="app-panel app-panel--main"
          style={{
            padding: activePage === "web" ? 0 : 20,
            minHeight: 360,
            overflow: activePage === "web" ? "hidden" : undefined,
          }}
        >
          <div style={{ display: activePage === "web" ? "block" : "none" }}>
            {activePage === "web" ? (
              <TheWebPage
                workspaceId={activeWorkspaceId}
                analysisModel={analysisModel}
                apiKeys={apiKeys}
                aiProfile={aiProfile}
                analysisKnowledge={analysisKnowledge}
              />
            ) : null}
          </div>
          <div
            style={{ display: activePage === "creatives" ? "block" : "none" }}
          >
            {creativesContent}
          </div>
          <div style={{ display: activePage === "post" ? "block" : "none" }}>
            {activePage === "post" ? (
              <PostPage
                apiKeys={effectiveApiKeys}
                analysisModel={postAnalysisModel}
                aiProfile={aiProfile}
                analysisKnowledge={analysisKnowledge}
                adCopyKnowledge={adCopyKnowledge}
                postTypeTags={postTypeTags}
                postExamples={postExamples}
                postCreatives={postCreatives}
                onPostCreativesChange={setPostCreatives}
                activityPreferences={userSettings?.activityPreferences}
                onActivityPreferencesChange={(next) =>
                  setUserSettings((prev) => ({
                    ...prev,
                    activityPreferences: Array.isArray(next) ? next : [],
                  }))
                }
                workspaceId={activeWorkspaceId}
                allowedFormats={["meta_ads", "organic", "native_story"]}
                defaultPostTypeFormat="meta_ads"
                defaultPromptChannels={["UGC"]}
                generateStoryImages
                pageTitle="Copy"
                pageSubtitle="Upload creatives to generate Meta Ads, Organic, and Native copy."
              />
            ) : null}
          </div>
          <div
            style={{ display: activePage === "assignment" ? "block" : "none" }}
          >
            {activePage === "assignment" ? (
              <AssignmentPage
                workspaceId={activeWorkspaceId}
                canBulk={orgAccess.features?.bulk_analyze !== false}
                canEditPrompt={Boolean(orgAccess.features?.edit_prompts)}
              />
            ) : null}
          </div>
          {/* Kept mounted: a 20-creative batch takes minutes, so switching
              tabs mid-run must not throw the chats away. */}
          <div style={{ display: activePage === "neuro" ? "block" : "none" }}>
            <NeuroPage
              apiKeys={effectiveApiKeys}
              aiProfile={aiProfile}
              analysisKnowledge={analysisKnowledge}
              adCopyKnowledge={adCopyKnowledge}
              tags={tags}
              analysisModel={analysisModel}
              generationAspectRatio={userSettings?.generationAspectRatio}
              workspaceId={activeWorkspaceId}
            />
          </div>
        </div>
      </Content>

      {/* Model footer bar */}
      <div className="app-footer-bar">
        {activePage === "creatives" && (
          <>
            <span className="app-footer-bar__item">
              <span>Analysis</span>
              <span>
                {userSettings.analysisModel || DEFAULT_POST_ANALYSIS_MODEL}
              </span>
            </span>
            <span className="app-footer-bar__item">
              <span>Variants</span>
              <span>
                {userSettings.variantModel || DEFAULT_VARIANT_MODEL}
              </span>
            </span>
            <span className="app-footer-bar__item">
              <span>Image</span>
              <span>{DEFAULT_IMAGE_MODEL}</span>
            </span>
            <span className="app-footer-bar__item">
              <span>Video</span>
              <span>{DEFAULT_VIDEO_MODEL}</span>
            </span>
          </>
        )}
        {activePage === "post" && (
          <>
            <span className="app-footer-bar__item">
              <span>Analysis</span>
              <span>
                {userSettings.postAnalysisModel || userSettings.analysisModel || DEFAULT_POST_ANALYSIS_MODEL}
              </span>
            </span>
            <span className="app-footer-bar__item">
              <span>Copy</span>
              <span>{DEFAULT_POST_COPY_MODEL}</span>
            </span>
          </>
        )}
        {activePage === "neuro" && (
          <>
            <span className="app-footer-bar__item">
              <span>Analysis</span>
              <span>
                {userSettings.analysisModel || DEFAULT_POST_ANALYSIS_MODEL}
              </span>
            </span>
            <span className="app-footer-bar__item">
              <span>Image</span>
              <span>{DEFAULT_IMAGE_MODEL}</span>
            </span>
          </>
        )}
      </div>

      <Modal
        title="Settings"
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        footer={null}
        width={900}
        centered
        className="settings-dashboard-modal"
        style={{ top: 24 }}
        styles={{
          body: {
            height: "82vh",
            maxHeight: "82vh",
            overflow: "hidden",
          },
        }}
      >
        <SettingsPage
          knowledgeDb={knowledgeDb}
          onKnowledgeChange={setKnowledgeDb}
          tags={tags}
          onTagsChange={setTags}
          postTypeTags={postTypeTags}
          onPostTypeTagsChange={setPostTypeTags}
          postExamples={postExamples}
          onPostExamplesChange={setPostExamples}
          brandLibrary={brandLibrary}
          onBrandChange={setBrandLibrary}
          onKnowledgeExtrasChange={() =>
            setKnowledgeExtrasVersion((prev) => prev + 1)
          }
          aiProfile={aiProfile}
          onAiProfileChange={setAiProfile}
          analysisKnowledge={analysisKnowledge}
          onAnalysisKnowledgeChange={setAnalysisKnowledge}
          adCopyKnowledge={adCopyKnowledge}
          onAdCopyKnowledgeChange={setAdCopyKnowledge}
          blogKnowledge={blogKnowledge}
          onBlogKnowledgeChange={setBlogKnowledge}
          blogSeo={blogSeo}
          onBlogSeoChange={setBlogSeo}
          apiKeys={apiKeys}
          apiKeyStatus={
            userSettings?.apiKeyStatus || defaultUserSettings.apiKeyStatus
          }
          onApiKeysChange={() =>
            setUserSettings((prev) => ({
              ...prev,
              apiKeys: { ...defaultUserSettings.apiKeys },
            }))
          }
          onApiKeyStatusChange={(next) =>
            setUserSettings((prev) => ({
              ...prev,
              apiKeys: { ...defaultUserSettings.apiKeys },
              apiKeyStatus: {
                ...defaultUserSettings.apiKeyStatus,
                ...(next || {}),
              },
            }))
          }
          analysisModel={analysisModel}
          postAnalysisModel={userSettings?.postAnalysisModel || ""}
          onPostAnalysisModelChange={(value) =>
            setUserSettings((prev) => ({
              ...prev,
              postAnalysisModel: value,
            }))
          }
          blogModel={userSettings?.blogModel || ""}
          onBlogModelChange={(value) =>
            setUserSettings((prev) => ({
              ...prev,
              blogModel: value,
            }))
          }
          workspaceApiKeys={workspaceApiKeys}
          workspaceApiKeyStatus={workspaceApiKeyStatus}
          workspaceShareApiKeys={workspaceShareApiKeys}
          onWorkspaceApiKeysChange={() =>
            setWorkspaceApiKeys({ ...defaultUserSettings.apiKeys })
          }
          onWorkspaceApiKeyStatusChange={setWorkspaceApiKeyStatus}
          onWorkspaceShareApiKeysChange={setWorkspaceShareApiKeys}
          modelStats={userSettings?.modelStats}
          activityPreferences={userSettings?.activityPreferences}
          onActivityPreferencesChange={(next) =>
            setUserSettings((prev) => ({ ...prev, activityPreferences: next }))
          }
          notificationPreferences={notificationSettings}
          notificationPermission={notificationPermission}
          onNotificationPreferencesChange={updateNotificationSettings}
          onRequestNotificationPermission={requestNotificationPermission}
          onActiveWorkspaceChange={setActiveWorkspaceId}
          syncStatus={syncStatus}
          googleDriveConnected={Boolean(userSettings?.googleDriveConnected)}
          onGoogleDriveDisconnect={() =>
            setUserSettings((prev) => ({ ...prev, googleDriveConnected: false }))
          }
        />
      </Modal>
      <Modal
        title={null}
        open={teamOpen}
        onCancel={() => setTeamOpen(false)}
        footer={null}
        width={1000}
        centered
        destroyOnClose
      >
        <TeamPermissions isAdmin={isAdmin} />
      </Modal>
      <Modal
        title="Account"
        open={accountOpen}
        onCancel={() => setAccountOpen(false)}
        footer={null}
        width={520}
        centered
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text strong>Account Profile</Text>
          <Text type="secondary">
            Update your profile details used across your workspace.
          </Text>
          <Card size="small" className="panel-card" loading={accountLoading}>
            <Form layout="vertical">
              <Form.Item label="Email">
                <Input value={accountProfile.email} disabled />
              </Form.Item>
              <Form.Item label="Name">
                <Input
                  value={accountProfile.name}
                  onChange={(event) =>
                    setAccountProfile((prev) => ({
                      ...prev,
                      name: event.target.value,
                    }))
                  }
                />
              </Form.Item>
              <Form.Item label="Company">
                <Input
                  value={accountProfile.company}
                  onChange={(event) =>
                    setAccountProfile((prev) => ({
                      ...prev,
                      company: event.target.value,
                    }))
                  }
                />
              </Form.Item>
              <Form.Item label="Website">
                <Input
                  value={accountProfile.website}
                  onChange={(event) =>
                    setAccountProfile((prev) => ({
                      ...prev,
                      website: event.target.value,
                    }))
                  }
                />
              </Form.Item>
              <Button
                type="primary"
                onClick={handleSaveAccountProfile}
                loading={accountSaving}
              >
                Save Profile
              </Button>
            </Form>
          </Card>
        </Space>
      </Modal>
    </Layout>
  );
};

const App = () => {
  const [isDarkMode, setIsDarkMode] = useState(true);
  // Keep the CSS theme (data-theme + page bg) in sync with isDarkMode at the
  // top level so pre-login screens (AuthPage, callbacks, loaders) match the
  // antd token theme. Without this the login card rendered light-on-white.
  React.useEffect(() => {
    document.body.dataset.theme = isDarkMode ? "dark" : "light";
    document.body.style.backgroundColor = isDarkMode ? "#0b0d12" : "#f6f7f9";
  }, [isDarkMode]);
  const [userSettings, setUserSettings] = useState(defaultUserSettings);
  const userSettingsTimer = useRef(null);
  const [syncStatus, setSyncStatus] = useState({
    settingsSaving: false,
    workspaceSaving: false,
    lastSettingsSyncAt: null,
    lastWorkspaceSyncAt: null,
    lastError: "",
  });
  const [authLoading, setAuthLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [sessionVersion, setSessionVersion] = useState(0);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const workspaceFlushRef = useRef(null);

  // Keep prompt stores wired to user_settings for DB persistence
  React.useEffect(() => {
    onPromptLibraryChange((data) =>
      setUserSettings((prev) => ({ ...prev, promptLibrary: data }))
    );
    onSectionOverridesChange((data) =>
      setUserSettings((prev) => ({ ...prev, promptOverrides: data }))
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setSessionWithVersion = React.useCallback((nextSession) => {
    setSession(nextSession);
    if (nextSession?.user?.id) {
      setSessionVersion((prev) => prev + 1);
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const isAppRoute = window.location.pathname === "/app";
    setShowDashboard(isAppRoute);
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleUnauthorized = async () => {
      try {
        const base = String(import.meta?.env?.VITE_API_BASE_URL || "").replace(/\/+$/, "");
        const res = await fetch(`${base}/api/auth/session`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          if (data?.user?.id) return;
        }
      } catch {
        return;
      }
      setSessionWithVersion(null);
      setShowDashboard(false);
    };
    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
    };
  }, [setSessionWithVersion]);

  const openDashboard = React.useCallback(() => {
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/app");
    }
    setShowDashboard(true);
  }, []);

  const flushUserSettings = React.useCallback(async () => {
    if (!session?.user?.id || !userSettingsTimer.current) return;
    clearTimeout(userSettingsTimer.current);
    userSettingsTimer.current = null;
    setSyncStatus((prev) => ({
      ...prev,
      settingsSaving: true,
      lastError: "",
    }));
    try {
      await saveUserSettings(session.user.id, userSettings);
      setSyncStatus((prev) => ({
        ...prev,
        settingsSaving: false,
        lastSettingsSyncAt: new Date().toISOString(),
      }));
    } catch (error) {
      setSyncStatus((prev) => ({
        ...prev,
        settingsSaving: false,
        lastError: error?.message || "Failed to save settings",
      }));
    }
  }, [userSettings, session?.user?.id]);

  React.useEffect(() => {
    if (!session?.user?.id) return;
    if (userSettingsTimer.current) {
      clearTimeout(userSettingsTimer.current);
    }
    userSettingsTimer.current = setTimeout(() => {
      setSyncStatus((prev) => ({
        ...prev,
        settingsSaving: true,
        lastError: "",
      }));
      saveUserSettings(session.user.id, userSettings)
        .then(() => {
          setSyncStatus((prev) => ({
            ...prev,
            settingsSaving: false,
            lastSettingsSyncAt: new Date().toISOString(),
          }));
        })
        .catch((error) => {
          setSyncStatus((prev) => ({
            ...prev,
            settingsSaving: false,
            lastError: error?.message || "Failed to save settings",
          }));
        });
    }, 500);
    return () => {
      if (userSettingsTimer.current) {
        clearTimeout(userSettingsTimer.current);
      }
    };
  }, [userSettings, session?.user?.id]);

  const handleLogout = React.useCallback(async () => {
    // Flush pending saves before logout
    const flushes = [flushUserSettings()];
    if (workspaceFlushRef.current) {
      flushes.push(workspaceFlushRef.current());
    }
    await Promise.allSettled(flushes);
    try {
      await logout();
    } catch (error) {
      // ignore logout errors
    }
    setSessionWithVersion(null);
    setShowDashboard(false);
  }, [flushUserSettings, setSessionWithVersion]);

  const handleAuthComplete = React.useCallback(
    async (payload) => {
      let nextSession = payload?.session
        ? payload.session
        : payload?.user
          ? { user: payload.user }
          : null;

      try {
        const refreshed = await getSession();
        const cookieSession = refreshed?.session
          ? refreshed.session
          : refreshed?.user
            ? { user: refreshed.user }
            : null;
        if (cookieSession?.user?.id) {
          nextSession = cookieSession;
        }
      } catch (error) {
        // ignore and fall back to login payload
      }

      if (nextSession?.user?.id) {
        setSessionWithVersion(nextSession);
        openDashboard();
        return;
      }

      setSessionWithVersion(null);
      setShowDashboard(false);
    },
    [openDashboard, setSessionWithVersion],
  );

  React.useEffect(() => {
    let mounted = true;
    getSession()
      .then((payload) => {
        if (!mounted) return;
        const nextSession = payload?.session
          ? payload.session
          : payload?.user
            ? { user: payload.user }
            : null;
        setSessionWithVersion(nextSession);
      })
      .catch(() => {
        if (mounted) setSessionWithVersion(null);
      })
      .finally(() => {
        if (mounted) setAuthLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (!session?.user?.id) return;
    let cancelled = false;
    const loadSettings = async () => {
      try {
        const loaded = await loadUserSettings(session.user.id);
        if (cancelled) return;
        setUserSettings(loaded);
        setIsDarkMode(Boolean(loaded.uiDarkMode));
        // Initialise prompt stores from persisted DB data
        initPromptLibrary(loaded.promptLibrary || {});
        initSectionOverrides(loaded.promptOverrides || {});
      } catch (error) {
        setSyncStatus((prev) => ({
          ...prev,
          lastError: error?.message || "Failed to load settings",
        }));
      }
    };
    loadSettings();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  React.useEffect(() => {
    const loadProfile = async () => {
      if (!session?.user?.id) {
        return;
      }
      setProfileLoading(true);
      try {
        await fetchProfile();
      } catch (error) {
        // ignore profile load errors for internal users
      } finally {
        setProfileLoading(false);
      }
    };
    loadProfile();
  }, [session?.user?.id]);

  const appTheme = React.useMemo(
    () => ({
      algorithm: isDarkMode
        ? antdTheme.darkAlgorithm
        : antdTheme.defaultAlgorithm,
      token: {
        // One accent, one neutral scale, one font. Comfortable — not micro.
        colorPrimary: isDarkMode ? "#8b7bf7" : "#6d5cf6",
        colorInfo: isDarkMode ? "#8b7bf7" : "#6d5cf6",
        colorSuccess: isDarkMode ? "#4ade80" : "#16a34a",
        colorWarning: isDarkMode ? "#facc15" : "#ca8a04",
        colorError: isDarkMode ? "#f87171" : "#dc2626",
        colorText: isDarkMode ? "#e8eaf0" : "#1f2430",
        colorTextSecondary: isDarkMode ? "#9aa2b4" : "#5b6472",
        colorBgLayout: isDarkMode ? "#0b0d12" : "#f6f7f9",
        colorBgContainer: isDarkMode ? "#15181f" : "#ffffff",
        colorBgElevated: isDarkMode ? "#1b1f28" : "#ffffff",
        colorBgSpotlight: isDarkMode ? "#1b1f28" : "#ffffff",
        colorBorder: isDarkMode
          ? "rgba(255, 255, 255, 0.10)"
          : "rgba(20, 24, 33, 0.12)",
        colorBorderSecondary: isDarkMode
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(20, 24, 33, 0.07)",
        colorSplit: isDarkMode
          ? "rgba(255, 255, 255, 0.07)"
          : "rgba(20, 24, 33, 0.08)",
        colorFillSecondary: isDarkMode ? "rgba(255,255,255,0.04)" : undefined,
        // Comfortable sizing — larger text + controls, generous radius.
        fontSize: 15,
        fontSizeLG: 17,
        fontSizeSM: 13,
        fontSizeHeading1: 30,
        fontSizeHeading2: 24,
        fontSizeHeading3: 19,
        fontSizeHeading4: 16,
        lineHeight: 1.6,
        borderRadius: 10,
        borderRadiusLG: 14,
        borderRadiusSM: 8,
        controlHeight: 40,
        controlHeightLG: 48,
        controlHeightSM: 32,
        padding: 18,
        paddingLG: 26,
        fontFamily:
          '"Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif',
        fontFamilyCode:
          '"JetBrains Mono", "SFMono-Regular", Consolas, "Liberation Mono", monospace',
        boxShadow: isDarkMode
          ? "0 1px 3px rgba(0,0,0,0.4)"
          : "0 1px 3px rgba(20,24,33,0.06)",
        boxShadowSecondary: isDarkMode
          ? "0 4px 16px rgba(0,0,0,0.5)"
          : "0 4px 16px rgba(20,24,33,0.08)",
        motionDurationMid: "0.15s",
        motionDurationSlow: "0.2s",
      },
      components: isDarkMode
        ? {
            Card: {
              headerBg: "#15181f",
              colorBgContainer: "#15181f",
              boxShadowTertiary: "none",
            },
            Modal: { contentBg: "#15181f", headerBg: "#15181f" },
            Divider: { colorSplit: "rgba(255,255,255,0.07)" },
            Input: {
              colorBgContainer: "#0f1218",
              activeBorderColor: "#8b7bf7",
              hoverBorderColor: "rgba(255,255,255,0.18)",
            },
            Select: {
              colorBgContainer: "#0f1218",
              colorBgElevated: "#1b1f28",
            },
            Button: {
              defaultBg: "#1b1f28",
              defaultBorderColor: "rgba(255,255,255,0.1)",
              defaultHoverBg: "#232833",
              defaultHoverBorderColor: "rgba(255,255,255,0.2)",
            },
            Segmented: {
              itemSelectedBg: "#8b7bf7",
              itemSelectedColor: "#0b0d12",
              trackBg: "#0f1218",
            },
            Tag: { defaultBg: "#1b1f28" },
            Slider: {
              railBg: "rgba(255,255,255,0.1)",
              railHoverBg: "rgba(255,255,255,0.15)",
            },
          }
        : {
            Segmented: {
              itemSelectedBg: "#6d5cf6",
              itemSelectedColor: "#ffffff",
            },
          },
    }),
    [isDarkMode],
  );

  if (authLoading) {
    return (
      <ConfigProvider theme={appTheme}>
        <LoadingScreen />
      </ConfigProvider>
    );
  }

  // Handle Google Drive OAuth callback
  if (window.location.pathname === "/auth/google/drive-callback") {
    return (
      <ConfigProvider theme={appTheme}>
        <GoogleDriveCallback
          onComplete={() => {
            setUserSettings((prev) => ({ ...prev, googleDriveConnected: true }));
            setSettingsOpen(true);
            window.history.replaceState({}, "", "/");
          }}
          onError={(errMsg) => {
            window.history.replaceState({}, "", "/");
            setSettingsOpen(true);
          }}
        />
      </ConfigProvider>
    );
  }

  // Handle Google OAuth callback route
  if (window.location.pathname === "/auth/google/callback") {
    return (
      <ConfigProvider theme={appTheme}>
        <GoogleAuthCallback
          onAuthComplete={handleAuthComplete}
          onError={(errMsg) => {
            // Redirect to auth page with error
            window.history.replaceState({}, "", "/");
          }}
        />
      </ConfigProvider>
    );
  }

  if (!session || !showDashboard) {
    return (
      <ConfigProvider theme={appTheme}>
        <AuthPage onAuthComplete={handleAuthComplete} />
      </ConfigProvider>
    );
  }

  // Bootstrap-only fallback. The authoritative admin flag is profiles.is_admin,
  // resolved inside AppInner via /api/org/me (user_metadata is no longer trusted
  // at all — it was self-writable).
  const isAdmin = hasAdminRole(session?.user);

  if (profileLoading) {
    return (
      <ConfigProvider theme={appTheme}>
        <LoadingScreen
          title="Loading"
          subtitle="Checking your account status."
        />
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={appTheme}>
      <AppInner
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        isAdmin={isAdmin}
        session={session}
        sessionVersion={sessionVersion}
        userSettings={userSettings}
        setUserSettings={setUserSettings}
        syncStatus={syncStatus}
        setSyncStatus={setSyncStatus}
        onLogout={handleLogout}
        flushUserSettings={flushUserSettings}
        workspaceFlushRef={workspaceFlushRef}
      />
      <StatusHub />
    </ConfigProvider>
  );
};

export default App;
